// On-screen keyboard for CONTROLLER players on desktop browsers. A focused
// <input> pops a system keyboard on consoles, phones and the TV app, but a
// desktop browser shows nothing — so a pad-only player could not log in, add
// a friend, chat in the lobby or type a room code. Each key is a plain
// <button> inside a [data-pad-modal] scope, so main.ts's padMenuLoop drives it
// with the same spatial navigation / A-to-click as every other menu; the
// [data-osk] tag adds the console shortcuts (X backspace, Y space).

type TextField = HTMLInputElement | HTMLTextAreaElement;

const LETTER_ROWS = ["1234567890", "qwertyuiop", "asdfghjkl@", "zxcvbnm_.-"];
const NUMBER_ROWS = ["123", "456", "789", "0"]; // number fields here are all positive integers ("-"/"." would sanitize the value to "")

let open: { root: HTMLElement; field: TextField; close: () => void } | null = null;

export const padKeyboardOpen = (): boolean => !!open;

/** Remove the keyboard (B / Esc / Enter / the field leaving the DOM). */
export function closePadKeyboard(): void {
  open?.close();
}

/** X / Y shortcuts from padMenuLoop. */
export function padKeyboardKey(action: "backspace" | "space"): void {
  if (!open) return;
  if (action === "backspace") edit(open.field, (v) => v.slice(0, -1));
  else edit(open.field, (v) => v + " ");
  refresh();
}

let refresh: () => void = () => { /* set while open */ };

function edit(field: TextField, fn: (v: string) => string): void {
  const max = field.maxLength > 0 ? field.maxLength : Infinity;
  const next = fn(field.value);
  if (next.length > max) return;
  field.value = next;
  field.dispatchEvent(new Event("input", { bubbles: true }));
}

export function openPadKeyboard(field: TextField): void {
  closePadKeyboard();
  const numeric = field instanceof HTMLInputElement && field.type === "number";
  const masked = field instanceof HTMLInputElement && field.type === "password";
  let shift = false;

  const root = document.createElement("div");
  root.className = "osk";
  root.setAttribute("data-pad-modal", "");
  root.setAttribute("data-osk", "");

  const label = (field.closest("form, .panel")?.querySelector("label")?.textContent ?? "").trim();
  const head = document.createElement("div");
  head.className = "osk-head";
  head.textContent = field.getAttribute("placeholder") || label || "Type";
  const preview = document.createElement("div");
  preview.className = "osk-preview";

  const keys: HTMLButtonElement[] = [];
  const key = (text: string, act: () => void, cls = ""): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `osk-key ${cls}`.trim();
    b.textContent = text;
    // mousedown would steal focus from the ring/field; keys are pad-or-click
    b.addEventListener("mousedown", (e) => e.preventDefault());
    b.onclick = () => { act(); refresh(); };
    return b;
  };

  const grid = document.createElement("div");
  grid.className = "osk-grid";
  for (const row of numeric ? NUMBER_ROWS : LETTER_ROWS) {
    const r = document.createElement("div");
    r.className = "osk-row";
    for (const ch of row) {
      const b = key(ch, () => edit(field, (v) => v + (shift ? ch.toUpperCase() : ch)));
      b.dataset.ch = ch;
      keys.push(b);
      r.append(b);
    }
    grid.append(r);
  }
  // land the ring on a letter, not on "1"
  (keys.find((b) => b.dataset.ch === (numeric ? "5" : "q")) ?? keys[0])?.setAttribute("data-pad-default", "");

  const bottom = document.createElement("div");
  bottom.className = "osk-row";
  const shiftKey = numeric ? null : key("⇧ Shift", () => { shift = !shift; }, "wide");
  const space = numeric ? null : key("Space", () => edit(field, (v) => v + " "), "wider");
  const back = key("⌫", () => edit(field, (v) => v.slice(0, -1)), "wide");
  const enter = key("⏎ Enter", () => submit(), "wide go");
  enter.setAttribute("data-pad-start", "");
  const done = key("Close", () => closePadKeyboard(), "wide");
  done.setAttribute("data-pad-back", "Close");
  bottom.append(...[shiftKey, space, back, enter, done].filter((b): b is HTMLButtonElement => !!b));
  grid.append(bottom);

  root.append(head, preview, grid);
  document.body.append(root);

  refresh = () => {
    preview.textContent = masked ? "•".repeat(field.value.length) : field.value;
    preview.classList.toggle("empty", !field.value);
    shiftKey?.classList.toggle("on", shift);
    for (const b of keys) {
      const ch = b.dataset.ch ?? "";
      b.textContent = shift ? ch.toUpperCase() : ch;
    }
  };
  refresh();

  const submit = (): void => {
    closePadKeyboard();
    // a <form> (log in / register) submits; other fields act on Enter keydown
    // (lobby chat, add-friend), exactly like a physical Enter press
    const form = field instanceof HTMLInputElement ? field.form : null;
    if (form) form.requestSubmit();
    else field.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", code: "Enter", bubbles: true }));
  };

  // the screen re-rendered / navigated away under us → don't strand the panel
  const watchdog = window.setInterval(() => { if (!field.isConnected) closePadKeyboard(); }, 300);
  const state = {
    root,
    field,
    close: () => {
      window.clearInterval(watchdog);
      root.remove();
      if (open === state) open = null;
      refresh = () => { /* closed */ };
      // hand the ring back to the field the player was typing into
      if (field.isConnected) field.focus({ preventScroll: true });
    },
  };
  open = state;
}
